import { auth } from '@/auth';

/**
 * Кэш выбора из справочников на время сессии.
 *
 * Схема справочника (атрибуты) меняется редко — выкладкой конфигурации или правкой в админке, — поэтому держится
 * {@link SCHEME_TTL}: открытия подряд обходятся без запроса, а переименованный атрибут не живёт в кэше долго.
 *
 * Строки — «показать сразу, проверить следом»: повторное открытие рисует прошлый ответ, а свежесть подтверждает
 * сервер (`gridItems` отдаёт ETag по Hash справочника, неизменившийся справочник отвечает браузеру 304 без
 * выборки). Ключ строк — всё, от чего зависит выборка: вычисленные фильтры и `formValues` формы, предикаты, поиск.
 * Другое значение поля-источника — другой ключ, чужой список не подставится.
 *
 * Тем же кэшем строк пользуется выпадающий список адресной книги: вместо имени справочника — `addressbook` и кэш
 * вкладки (`useAbookComboboxItems`).
 */

const ITEMS_CAPACITY = 50;
const SCHEME_TTL = 5 * 60 * 1000;

interface IItemsEntry {
    dictName: string;
    value?: unknown;
    pending?: Promise<unknown>;
}

const schemes = new Map<string, { pending: Promise<unknown>; at: number; value?: unknown }>();
const items = new Map<string, IItemsEntry>();

const freshScheme = (key: string) => {
    const cached = schemes.get(key);
    return cached && Date.now() - cached.at < SCHEME_TTL ? cached : undefined;
};

/** Схема справочника: одна загрузка на {@link SCHEME_TTL}; отказ не запоминается. */
export const cachedDictionaryScheme = <T>(key: string, load: () => Promise<T>): Promise<T> => {
    const cached = freshScheme(key);
    if (cached) return cached.pending as Promise<T>;
    // синхронный сбой загрузчика — тоже отказ промиса, а не исключение у вызывающего
    const entry: { pending: Promise<T>; at: number; value?: T } = {
        pending: Promise.resolve().then(load),
        at: Date.now(),
    };
    schemes.set(key, entry);
    entry.pending.then(
        (value) => (entry.value = value),
        () => {
            if (schemes.get(key) === entry) schemes.delete(key);
        },
    );
    return entry.pending;
};

/** Уже загруженная схема — без ожидания: поле решает, как себя показать, до первой отрисовки. */
export const peekDictionaryScheme = <T>(key: string): T | undefined => freshScheme(key)?.value as T | undefined;

/** Справочник входит в ключ здесь, а не у вызывающих: два справочника с одной выборкой не делят ответ. */
const itemsKey = (dictName: string, key: string) => `${dictName}\n${key}`;

/** Последний ответ по ключу — для показа до проверки свежести. */
export const peekDictionaryItems = <T>(dictName: string, key: string): T | undefined =>
    items.get(itemsKey(dictName, key))?.value as T | undefined;

/** Строки справочника: одновременные вызовы делят запрос, ответ запоминается под ключом. */
export const loadDictionaryItems = <T>(dictName: string, selection: string, load: () => Promise<T>): Promise<T> => {
    const key = itemsKey(dictName, selection);
    const entry = items.get(key) ?? { dictName };
    if (entry.pending) return entry.pending as Promise<T>;
    const pending = load()
        .then((value) => {
            if (items.get(key) === entry) entry.value = value;
            return value;
        })
        .finally(() => {
            if (entry.pending === pending) entry.pending = undefined;
        });
    entry.pending = pending;
    // свежий ключ — в конец: вытесняются давно не открытые списки
    items.delete(key);
    items.set(key, entry);
    for (const [oldKey, oldEntry] of items) {
        if (items.size <= ITEMS_CAPACITY) break;
        if (!oldEntry.pending) items.delete(oldKey);
    }
    return pending;
};

/** Справочник изменили из приложения — прежние ответы по нему не показываем. */
export const invalidateDictionaryItems = (dictName: string) => {
    for (const [key, entry] of items) {
        if (entry.dictName === dictName) items.delete(key);
    }
};

export const resetDictionaryCache = () => {
    schemes.clear();
    items.clear();
};

// Строки зависят от прав и атрибутов пользователя — со сменой сессии кэш уходит целиком.
auth.onSessionEnd(resetDictionaryCache);
